import React from 'react';
import { ArrowRightIcon, DownloadIcon, CheckCircleIcon, ClockIcon } from 'lucide-react';
export function Hero() {
  return <section className="relative min-h-screen flex items-center justify-center overflow-hidden" data-id="element-219">
      <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 text-center" data-id="element-220">
        {/* Urgency bar */}
        <div className="mb-6 inline-flex items-center gap-2 px-4 py-2 bg-orange-500/20 border border-orange-500/40 rounded-full" data-id="element-221">
          <ClockIcon className="w-4 h-4 text-orange-400" data-id="element-222" />
          <span className="text-sm font-semibold text-orange-300" data-id="element-223">
            Next Cancun Cohort: March 15-18, 2024 • Only 8 Spots Remaining
          </span>
        </div>

        {/* Trust bar - reordered */}
        <div className="mb-8 flex flex-wrap items-center justify-center gap-4 text-sm text-slate-400" data-id="element-224">
          <span className="px-3 py-1 bg-slate-800/50 rounded-full border border-slate-700" data-id="element-225">
            Delivered in Cancun, Mexico
          </span>
          <span className="px-3 py-1 bg-slate-800/50 rounded-full border border-slate-700" data-id="element-226">
            Powered by OpenAI, Claude, Perplexity
          </span>
          <span className="px-3 py-1 bg-slate-800/50 rounded-full border border-slate-700" data-id="element-227">
            Stanford • Wharton • INSEAD • MIT
          </span>
        </div>

        {/* Main headline */}
        <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight" data-id="element-228">
          Stop Watching AI Happen.
          <br data-id="element-229" />
          <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent" data-id="element-230">
            Start Leading It.
          </span>
        </h1>

        {/* Subheadline */}
        <p className="text-xl md:text-2xl text-slate-300 mb-8 max-w-4xl mx-auto leading-relaxed" data-id="element-231">
          A 48-hour AI immersion for leaders who refuse to be left behind. Leave
          with a 90-day roadmap, working prototypes, and the confidence to lead
          your company's AI transformation—set in luxurious Cancun beachfront
          resorts.
        </p>

        {/* Social proof metrics */}
        <div className="flex flex-wrap justify-center gap-8 mb-12 text-center" data-id="element-232">
          <div data-id="element-233">
            <div className="text-3xl font-bold text-cyan-400" data-id="element-234">500+</div>
            <div className="text-sm text-slate-400" data-id="element-235">Leaders Transformed</div>
          </div>
          <div data-id="element-236">
            <div className="text-3xl font-bold text-purple-400" data-id="element-237">48hrs</div>
            <div className="text-sm text-slate-400" data-id="element-238">Intensive Learning</div>
          </div>
          <div data-id="element-239">
            <div className="text-3xl font-bold text-green-400" data-id="element-240">98%</div>
            <div className="text-sm text-slate-400" data-id="element-241">Would Recommend</div>
          </div>
          <div data-id="element-242">
            <div className="text-3xl font-bold text-orange-400" data-id="element-243">1:8</div>
            <div className="text-sm text-slate-400" data-id="element-244">
              Faculty-to-Student Ratio
            </div>
          </div>
        </div>

        {/* CTAs with stronger urgency */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8" data-id="element-245">
          <button className="group px-8 py-4 bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-semibold rounded-lg hover:shadow-2xl hover:shadow-cyan-500/50 transition-all duration-300 flex items-center gap-2" data-id="element-246">
            Apply Now - 8 Spots Left
            <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-247" />
          </button>
          <button className="px-8 py-4 bg-slate-800/50 text-white font-semibold rounded-lg border border-slate-700 hover:bg-slate-800 transition-all duration-300 flex items-center gap-2" data-id="element-248">
            <DownloadIcon className="w-5 h-5" data-id="element-249" />
            Download Full Agenda
          </button>
        </div>

        {/* Trust indicators */}
        <div className="flex flex-wrap justify-center gap-4 text-sm text-slate-500 mb-12" data-id="element-250">
          <div className="flex items-center gap-2" data-id="element-251">
            <CheckCircleIcon className="w-4 h-4 text-green-400" data-id="element-252" />
            <span data-id="element-253">No credit card required to apply</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-254">
            <CheckCircleIcon className="w-4 h-4 text-green-400" data-id="element-255" />
            <span data-id="element-256">15-minute interview process</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-257">
            <CheckCircleIcon className="w-4 h-4 text-green-400" data-id="element-258" />
            <span data-id="element-259">60-day money-back guarantee</span>
          </div>
        </div>

        {/* Partner logos section */}
        <div className="max-w-5xl mx-auto" data-id="element-260">
          <p className="text-sm text-slate-500 mb-6 uppercase tracking-wider" data-id="element-261">
            Trusted by Leaders From
          </p>

          {/* AI Partners - Lighter gray */}
          <div className="flex flex-wrap items-center justify-center gap-8 mb-8 text-slate-400" data-id="element-262">
            <span className="text-lg font-semibold opacity-70" data-id="element-263">OpenAI</span>
            <span className="text-lg font-semibold opacity-70" data-id="element-264">Perplexity</span>
            <span className="text-lg font-semibold opacity-70" data-id="element-265">Claude</span>
            <span className="text-lg font-semibold opacity-70" data-id="element-266">Google</span>
            <span className="text-lg font-semibold opacity-70" data-id="element-267">Microsoft</span>
            <span className="text-lg font-semibold opacity-70" data-id="element-268">Cohere</span>
          </div>

          {/* Universities - Slightly darker gray */}
          <div className="flex flex-wrap items-center justify-center gap-6 text-slate-500 text-sm" data-id="element-269">
            <span className="opacity-60" data-id="element-270">Stanford</span>
            <span className="opacity-60" data-id="element-271">Wharton</span>
            <span className="opacity-60" data-id="element-272">INSEAD</span>
            <span className="opacity-60" data-id="element-273">Ivey</span>
            <span className="opacity-60" data-id="element-274">MIT</span>
            <span className="opacity-60" data-id="element-275">Waterloo</span>
            <span className="opacity-60" data-id="element-276">NUS</span>
            <span className="opacity-60" data-id="element-277">University of Toronto</span>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce" data-id="element-278">
        <div className="w-6 h-10 border-2 border-slate-600 rounded-full flex items-start justify-center p-2" data-id="element-279">
          <div className="w-1 h-3 bg-slate-600 rounded-full" data-id="element-280"></div>
        </div>
      </div>
    </section>;
}